import { css } from '@emotion/core';
import { Text, TextLink } from '@weave/design-system';
import { theme } from '@weave/theme';
import { FC, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { GetPortingAuditSummaryRecord } from '../../../apis/porting/porting.types';
import { Page } from '../../../components/shared';
import { injectParams, ONBOARDING } from '../../../constants/paths';
import { formatDate } from '../../../helpers/date';
import { PortingQueries } from '../../../apis/porting/porting.queries';
import { Loader } from '../../../components/shared/loader/loader.component';
import { PortDetailCompare } from './PortDetailCompare';

export const PortAuditRevisionDetail: FC = () => {
  const history = useHistory();
  const {
    id: locationId,
    portingDataId,
    revisionId,
  } = useParams<{ id: string; portingDataId: string; revisionId: string }>();

  const handleBack = () => {
    history.push(
      injectParams(ONBOARDING.portingV2Detail, { id: locationId, portingDataId })
    );
  };
  const { data, isLoading } = PortingQueries.useGetPortingAuditRevisionDetails({
    portingDataId,
    revisionId,
  });

  const { revisionMetadata, newPortingData, oldPortingData } = useMemo(() => {
    return {
      revisionMetadata: data
        ? {
            revisionId: data.revisionDetails?.id,
            changedBy: data.revisionDetails?.changesInitiatedBy,
            changedAt: data.revisionDetails?.changesInitiatedAt,
          }
        : null,
      newPortingData: data?.revisionDetails,
      oldPortingData: data?.previousRevisionDetails,
    };
  }, [data]);
  return (
    <Page title="Port Order Change Log">
      <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
        <TextLink onClick={handleBack} css={{ maxWidth: 'max-content' }}>
          {'< Back'}
        </TextLink>
        {isLoading && <Loader />}
        {!!revisionMetadata && <PortRevisionSummary {...revisionMetadata} />}
        {!!data && (
          <PortDetailCompare
            newPortingData={newPortingData}
            oldPortingData={oldPortingData}
          />
        )}
      </div>
    </Page>
  );
};

const PortRevisionSummary: FC<GetPortingAuditSummaryRecord> = ({
  revisionId,
  changedBy,
  changedAt,
}) => {
  return (
    <section css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5) }}>
      <div css={summaryRowStyles}>
        <Text as="div" color="light" size="medium">
          Log ID
        </Text>
        <Text as="div" color="primary" size="medium">
          {revisionId}
        </Text>
      </div>
      <div css={summaryRowStyles}>
        <Text as="div" color="light" size="medium">
          Date
        </Text>
        <Text as="div" size="medium">
          {formatDate(changedAt, 'llll', true)}
        </Text>
      </div>
      <div css={summaryRowStyles}>
        <Text as="div" color="light" size="medium">
          User
        </Text>
        <Text as="div" size="medium">
          {changedBy}
        </Text>
      </div>
    </section>
  );
};

const summaryRowStyles = css({
  display: 'grid',
  gridTemplateColumns: '82px 1fr',
  gap: theme.spacing(1),
});
